export type DocumentScope = 'EMPLOYEE' | 'COMPANY'

/**
 * How a document stands today.
 *
 * Derived by the server from the expiry and the type's alert schedule, never stored — so
 * these values are always current and the client must not try to recompute them from
 * `expiryDate` alone. `UNKNOWN` means no expiry was recorded on a type that does not
 * demand one; nothing can be said about it either way.
 */
export type DocumentStatus = 'VALID' | 'EXPIRING' | 'EXPIRED' | 'UNKNOWN'

export interface DocumentTypeView {
  id: string
  legalEntityId: string | null
  scope: DocumentScope
  code: string
  name: string
  description: string | null
  requiresNumber: boolean
  requiresExpiry: boolean
  requiresFile: boolean
  /** Days before expiry to warn, furthest first. Empty means this type never alerts. */
  alertDaysBefore: number[]
  /**
   * Null on every seeded type, deliberately.
   *
   * Validity varies by emirate, free zone, nationality and category; a default here would
   * look authoritative while driving real renewal dates. The same goes for the two below.
   */
  validityMonths: number | null
  renewalLeadDays: number | null
  typicalFee: number | null
  feeCurrency: string | null
  statutoryReference: string | null
  seeded: boolean
  seedVersion: number | null
  active: boolean
  sortOrder: number
}

export interface EmployeeDocumentView {
  id: string
  personId: string
  documentTypeId: string
  documentTypeCode: string | null
  documentTypeName: string | null
  /** Only ever populated by the single-document endpoint, which logs the access. */
  documentNumber: string | null
  issuingCountry: string | null
  issuingAuthority: string | null
  issueDate: string | null
  expiryDate: string | null
  storedFileId: string | null
  status: DocumentStatus
  daysUntilExpiry: number | null
  verifiedAt: string | null
  notes: string | null
}

export interface CompanyDocumentView {
  id: string
  legalEntityId: string
  establishmentId: string | null
  documentTypeId: string
  documentTypeCode: string | null
  documentTypeName: string | null
  documentNumber: string | null
  issuingAuthority: string | null
  issueDate: string | null
  expiryDate: string | null
  storedFileId: string | null
  status: DocumentStatus
  daysUntilExpiry: number | null
  notes: string | null
}

export interface DocumentSeedResult {
  legalEntityId: string
  typesCreated: number
  message: string
}

/**
 * Status → badge tone.
 *
 * Expiring is orange rather than red on purpose: red is for something that has already
 * gone wrong, and a passport with sixty days left has not. Making both red teaches people
 * that red means "eventually", which is the reading you cannot afford on the ones that
 * have actually lapsed.
 */
export const DOCUMENT_STATUS_TONE = {
  VALID: 'success',
  EXPIRING: 'warning',
  EXPIRED: 'danger',
  UNKNOWN: 'neutral',
} as const

export const DOCUMENT_STATUS_LABEL: Record<DocumentStatus, string> = {
  VALID: 'Valid',
  EXPIRING: 'Expiring',
  EXPIRED: 'Expired',
  UNKNOWN: 'No expiry recorded',
}

/** "in 45 days", "today", "12 days ago" — the phrasing a person would use. */
export function expiryPhrase(days: number | null): string {
  if (days === null) return '—'
  if (days === 0) return 'today'
  if (days > 0) return `in ${days} ${days === 1 ? 'day' : 'days'}`
  const past = Math.abs(days)
  return `${past} ${past === 1 ? 'day' : 'days'} ago`
}
